/** Where the sun sits in the sky for a moment and place — the NOAA solar
 * position algorithm (https://gml.noaa.gov/grad/solcalc/solareqns.PDF),
 * good to a fraction of a degree. Pure math, offline. Direct port of
 * ios/Dustlark/Map/SolarPosition.swift — keep in lockstep with that file.
 * Feeds the outdoors look's live hillshade so its illumination follows the
 * real sun (see injectLiveSun in firstlight-looks.ts). */

export interface SunPosition {
  /** Compass direction the sun sits in, degrees clockwise from north. */
  azimuth: number;
  /** Degrees above (positive) or below (negative) the horizon. */
  altitude: number;
}

function deg2rad(d: number): number {
  return (d * Math.PI) / 180;
}

function rad2deg(r: number): number {
  return (r * 180) / Math.PI;
}

function normalizeDegrees(d: number): number {
  const m = d % 360;
  return m < 0 ? m + 360 : m;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** Mirrors Swift's Calendar-based minutesOfDayUTC(_:): UTC hour/minute/
 * whole-second, no fractional milliseconds. */
function minutesOfDayUTC(date: Date): number {
  return date.getUTCHours() * 60 + date.getUTCMinutes() + date.getUTCSeconds() / 60;
}

/** Sun azimuth/altitude for a moment and place. Line-for-line port of
 * SolarPosition.position(at:on:). */
export function solarPosition(date: Date, lat: number, lon: number): SunPosition {
  // Unix time already carries the fractional day, so this IS the Julian
  // Date — no calendar month/day decomposition needed.
  const jd = date.getTime() / 86400000 + 2440587.5;
  const t = (jd - 2451545.0) / 36525.0;

  const l0 = normalizeDegrees(280.46646 + t * (36000.76983 + t * 0.0003032));
  const m = 357.52911 + t * (35999.05029 - 0.0001537 * t);
  const eccent = 0.016708634 - t * (0.000042037 + 0.0000001267 * t);
  const mRad = deg2rad(m);
  const sunEqCtr =
    Math.sin(mRad) * (1.914602 - t * (0.004817 + 0.000014 * t)) +
    Math.sin(2 * mRad) * (0.019993 - 0.000101 * t) +
    Math.sin(3 * mRad) * 0.000289;
  const trueLong = l0 + sunEqCtr;
  const omega = 125.04 - 1934.136 * t;
  const appLong = trueLong - 0.00569 - 0.00478 * Math.sin(deg2rad(omega));

  const meanObliq =
    23 + (26 + (21.448 - t * (46.815 + t * (0.00059 - t * 0.001813))) / 60) / 60;
  const obliqCorr = meanObliq + 0.00256 * Math.cos(deg2rad(omega));
  const declin = rad2deg(
    Math.asin(Math.sin(deg2rad(obliqCorr)) * Math.sin(deg2rad(appLong))),
  );

  const y = Math.pow(Math.tan(deg2rad(obliqCorr / 2)), 2);
  const eqTime =
    4 *
    rad2deg(
      y * Math.sin(2 * deg2rad(l0)) -
        2 * eccent * Math.sin(mRad) +
        4 * eccent * y * Math.sin(mRad) * Math.cos(2 * deg2rad(l0)) -
        0.5 * y * y * Math.sin(4 * deg2rad(l0)) -
        1.25 * eccent * eccent * Math.sin(2 * mRad),
    );

  let trueSolarTime = (minutesOfDayUTC(date) + eqTime + 4 * lon) % 1440;
  if (trueSolarTime < 0) trueSolarTime += 1440;
  const hourAngle = trueSolarTime / 4 - 180;

  const latR = deg2rad(lat);
  const declinR = deg2rad(declin);
  const hourAngleR = deg2rad(hourAngle);
  const cosZenith = clamp(
    Math.sin(latR) * Math.sin(declinR) + Math.cos(latR) * Math.cos(declinR) * Math.cos(hourAngleR),
    -1,
    1,
  );
  const zenith = rad2deg(Math.acos(cosZenith));
  const altitude = 90 - zenith;

  const zenithR = deg2rad(zenith);
  const azDenom = Math.cos(latR) * Math.sin(zenithR);
  let azimuth: number;
  if (Math.abs(azDenom) < 1e-6) {
    // Sun (near) directly overhead — azimuth is undefined; pick the
    // pole-facing convention rather than divide by ~zero.
    azimuth = lat >= 0 ? 180 : 0;
  } else {
    const azArg = clamp(
      (Math.sin(latR) * Math.cos(zenithR) - Math.sin(declinR)) / azDenom,
      -1,
      1,
    );
    const azBase = rad2deg(Math.acos(azArg));
    azimuth = hourAngle > 0 ? normalizeDegrees(azBase + 180) : normalizeDegrees(540 - azBase);
  }

  return { azimuth, altitude };
}
